import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Button,
  Chip,
  Tooltip,
  Grow,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import axios from 'src/utils/axios';
import { fDateTime, fToNow } from 'src/utils/formatTime';
import { Z_INDEX } from 'src/constants/zIndex';
import Label from 'src/components/Label';
import { MAvatar } from 'src/components/@material-extend';
import createAvatar from 'src/utils/createAvatar';
import pageContextLabel from 'src/utils/pageContextLabel';
import { useSupportHours } from 'src/hooks/useSupportHours';
import ChatTranscriptView, { TranscriptMessage } from './ChatTranscriptView';

interface TicketData {
  key: string | null;
  url: string | null;
  status: string;
}

interface ChatSessionData {
  contact_id: string;
  page_context: string;
  initiation_timestamp: string | null;
  disconnect_timestamp: string | null;
  disconnect_reason: string;
  raw_transcript: TranscriptMessage[];
  created_at: string;
  ticket: TicketData | null;
  property_name: string | null;
  cert_id: number | null;
}

interface Props {
  onStartNew: (intent?: 'agent' | 'report_issue') => Promise<void>;
  onClose: () => void;
}

const RECENT_LIMIT = 3;

export default function ChatLauncher({
  onStartNew,
  onClose,
}: Props): React.ReactElement {
  const [sessions, setSessions] = useState<ChatSessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingSession, setViewingSession] = useState<ChatSessionData | null>(
    null
  );
  const [starting, setStarting] = useState<'agent' | 'report_issue' | null>(
    null
  );
  const [startError, setStartError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const lastIntentRef = useRef<'agent' | 'report_issue'>('agent');
  const { isOpen: inHours, nextOpenLabel } = useSupportHours();

  // Entrance animation + dialog focus management.
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useRef(
    typeof window !== 'undefined' &&
      !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ).current;

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Animate in on mount and move focus into the dialog so keyboard and
  // screen-reader users land inside the panel. This is a NON-modal dialog
  // (aria-modal=false) — we deliberately do not trap focus, since the
  // widget floats in the corner and does not block the underlying page.
  useEffect(() => {
    setOpen(true);
    const raf = requestAnimationFrame(() => {
      panelRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  };

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `/connect/chat_sessions/?limit=${RECENT_LIMIT}`
      );
      setSessions(res.data.sessions || []);
    } catch {
      setError('Could not load chat history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSelectSession = (session: ChatSessionData): void => {
    setViewingSession(session);
  };

  const handleStartNew = async (
    intent: 'agent' | 'report_issue' = 'agent'
  ): Promise<void> => {
    lastIntentRef.current = intent;
    setStarting(intent);
    setStartError(null);
    try {
      await onStartNew(intent);
      // On success, startConnectChat() replaces this component in the
      // shared root before its promise resolves — no further state
      // updates needed (and mountedRef guards against the unmounted-set
      // warning if it somehow resolves after unmount).
    } catch {
      if (mountedRef.current) {
        setStartError('Chat unavailable — email support@prontohousing.com');
        setStarting(null);
      }
    }
  };

  return (
    <Grow
      in={open}
      style={{ transformOrigin: 'bottom right' }}
      timeout={reduceMotion ? 0 : undefined}
    >
      <Paper
        ref={panelRef}
        role="dialog"
        aria-modal={false}
        aria-label="Pronto Support"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        elevation={0}
        sx={{
          width: 360,
          // Programmatic focus target (tabIndex -1) — suppress the ring on
          // the container itself; interactive children keep their own.
          '&:focus': { outline: 'none' },
          // The list view hugs its content (3 fixed recent items — no need
          // for the widget's full 500px chat height); the transcript view
          // needs the full height to show a scrollable message history.
          height: viewingSession ? 500 : 'auto',
          maxHeight: 500,
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: Z_INDEX.MAX,
          // NOTE: this launcher renders in a separate React root on
          // document.body (see useConnectChat), OUTSIDE the app's
          // ThemeProvider — so it falls back to MUI's default theme. The
          // Minimal-template extensions (theme.customShadows,
          // theme.shape.borderRadiusMd) do NOT exist here; using them throws
          // at runtime and blanks the widget. Use literal values instead.
          borderRadius: '16px',
          // Clip the blue header + dividers to the rounded corners — without
          // this the square-cornered header overflows the Paper's radius and
          // the top two corners read sharp.
          overflow: 'hidden',
          // Soft ambient shadow (mirrors the theme's customShadows.z24 value)
          // so the widget floats above the page instead of being stamped on.
          boxShadow:
            '0 0 4px 0 rgba(145, 158, 171, 0.24), 0 24px 48px 0 rgba(145, 158, 171, 0.24)',
        }}
      >
        {viewingSession ? (
          <ChatTranscriptView
            title={
              viewingSession.initiation_timestamp
                ? fDateTime(viewingSession.initiation_timestamp)
                : 'Chat'
            }
            subtitle={pageContextLabel(viewingSession.page_context)}
            messages={viewingSession.raw_transcript}
            onBack={() => setViewingSession(null)}
          />
        ) : (
          <>
            {/* Header */}
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  Pronto Support
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.8, display: 'block', mt: 0.25 }}
                >
                  {inHours
                    ? 'Typically replies in a few minutes'
                    : `Back ${nextOpenLabel ?? 'soon'}`}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label="Close support panel"
                sx={{ color: 'inherit' }}
                onClick={onClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Body — fixed, no scroll. RECENT_LIMIT keeps this to a size
              that always fits the panel without needing to scroll. */}
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  pb: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Recent Support Activity
                </Typography>
                {/* Section action → full history (My Activity tab in the hub).
                    Real anchor: this portal root is outside the React Router
                    tree, so useNavigate is unavailable. */}
                {!loading && !error && sessions.length > 0 && (
                  <Link
                    href="/workbench/support/hub/activity"
                    variant="caption"
                    underline="hover"
                  >
                    See all
                  </Link>
                )}
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : error ? (
                <Box sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    {error}
                  </Typography>
                  <Button size="small" variant="text" onClick={fetchSessions}>
                    Try again
                  </Button>
                </Box>
              ) : sessions.length === 0 ? (
                <Box
                  sx={{
                    px: 2,
                    py: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <ForumOutlinedIcon
                    sx={{ fontSize: 36, color: 'text.disabled' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    No conversations yet
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Start a chat or report an issue and it&rsquo;ll show up here
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {sessions.map((session) => {
                    const isOpen = !session.disconnect_timestamp;
                    const hasTicket = session.ticket && session.ticket.key;
                    const contextLabel = pageContextLabel(session.page_context);
                    const absoluteTime = session.initiation_timestamp
                      ? fDateTime(session.initiation_timestamp)
                      : null;
                    const relativeTime = session.initiation_timestamp
                      ? fToNow(session.initiation_timestamp)
                      : null;

                    // Derive agent name from transcript for avatar.
                    const transcript = session.raw_transcript || [];
                    const firstAgentMsg = transcript.find(
                      (m) => m.participantRole === 'AGENT'
                    );
                    const agentName = firstAgentMsg?.displayName || '';
                    const avatarData = agentName
                      ? createAvatar(agentName)
                      : { name: '', color: 'default' as const };

                    // Readable, single-string label for screen readers, so the
                    // row announces as one coherent phrase instead of disjoint
                    // date/context/pill fragments.
                    const rowAriaLabel = [
                      hasTicket ? 'Reported issue' : 'Live chat',
                      hasTicket && session.ticket?.key
                        ? `${session.ticket.key}, ${session.ticket.status}`
                        : '',
                      contextLabel,
                      relativeTime || 'unknown time',
                    ]
                      .filter(Boolean)
                      .join(', ');

                    return (
                      <ListItemButton
                        key={session.contact_id}
                        onClick={() => handleSelectSession(session)}
                        aria-label={rowAriaLabel}
                        divider
                      >
                        {hasTicket ? (
                          <MAvatar
                            color="info"
                            sx={{ width: 32, height: 32, mr: 1.5 }}
                          >
                            <ConfirmationNumberOutlinedIcon
                              sx={{ fontSize: 18 }}
                            />
                          </MAvatar>
                        ) : agentName ? (
                          <MAvatar
                            color={avatarData.color}
                            sx={{ width: 32, height: 32, mr: 1.5 }}
                          >
                            {avatarData.name}
                          </MAvatar>
                        ) : (
                          <MAvatar
                            color="primary"
                            sx={{ width: 32, height: 32, mr: 1.5 }}
                          >
                            <ChatIcon sx={{ fontSize: 18 }} />
                          </MAvatar>
                        )}
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {relativeTime ? (
                                <Tooltip title={absoluteTime ?? ''}>
                                  <Typography variant="body2" fontWeight={500}>
                                    {relativeTime}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" fontWeight={500}>
                                  Unknown date
                                </Typography>
                              )}
                              {isOpen && !hasTicket && (
                                <Chip
                                  label="Active"
                                  size="small"
                                  color="success"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {contextLabel}
                              </Typography>
                              {hasTicket && (
                                <Label
                                  color={
                                    session.ticket!.status === 'Closed'
                                      ? 'default'
                                      : 'warning'
                                  }
                                  variant="ghost"
                                >
                                  {session.ticket!.key} ·{' '}
                                  {session.ticket!.status}
                                </Label>
                              )}
                              {!hasTicket &&
                                session.ticket &&
                                !session.ticket.key && (
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                  >
                                    Submitting…
                                  </Typography>
                                )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 1.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              {startError && (
                <Box sx={{ mb: 1, textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {startError}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleStartNew(lastIntentRef.current)}
                    disabled={starting !== null}
                  >
                    Try again
                  </Button>
                </Box>
              )}
              {inHours ? (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={
                      starting === 'agent' ? (
                        <CircularProgress
                          size={16}
                          thickness={6}
                          color="inherit"
                        />
                      ) : (
                        <ChatIcon />
                      )
                    }
                    onClick={() => handleStartNew('agent')}
                    disabled={starting !== null}
                    sx={{ mb: 1 }}
                  >
                    Chat with a live agent
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={
                      starting === 'report_issue' ? (
                        <CircularProgress
                          size={16}
                          thickness={6}
                          color="inherit"
                        />
                      ) : (
                        <ConfirmationNumberOutlinedIcon />
                      )
                    }
                    onClick={() => handleStartNew('report_issue')}
                    disabled={starting !== null}
                  >
                    Report an issue
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={
                      starting === 'report_issue' ? (
                        <CircularProgress
                          size={16}
                          thickness={6}
                          color="inherit"
                        />
                      ) : (
                        <ConfirmationNumberOutlinedIcon />
                      )
                    }
                    onClick={() => handleStartNew('report_issue')}
                    disabled={starting !== null}
                    sx={{ mb: 1 }}
                  >
                    Report an issue
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ChatIcon />}
                    onClick={() => handleStartNew('agent')}
                    disabled
                  >
                    Chat with a live agent
                  </Button>
                  {/* Off-hours: keep the disabled chat button for
                      discoverability; one muted line notes when it opens.
                      Overall hours/status already live in the header. */}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: 'block', textAlign: 'center', mt: 0.75 }}
                  >
                    Live agent chat opens {nextOpenLabel ?? 'soon'}
                  </Typography>
                </>
              )}
            </Box>
          </>
        )}
      </Paper>
    </Grow>
  );
}
